import React from 'react';
import PropTypes from 'prop-types';
import Position, { RootRef } from '@ids/position';
import IDSContext from '@ids/context';
import ZIndex from '@ids/zindex';
import { styleOverrideReason } from '@ids/core';
import {
  Wrapper,
  StyledSuccessIcon,
  Content,
  StyledLinkActionButton,
  StyledCloseIconControl
} from './StyledInlineMessage';

const { bool, func, string, node, oneOfType, number, object } = PropTypes;

/**
 *
 */
class InlineMessage extends React.Component {
  static propTypes = {
    /**
     * Test automation ID.
     */
    automationId: string,
    /**
     * Show different UI if it is a static message
     */
    isStatic: bool,
    /**
     * provide alignment for popover
     */
    alignment: string,
    /**
     * provide position for popover
     */
    position: string,
    /**
     * Target that triggers the non static inline message.
     */
    children: oneOfType([string, node]),
    /**
     * show or hide popover at initial state
     */
    // eslint-disable-next-line react/boolean-prop-naming
    open: bool,
    /**
     * show/hide close button. Accessibility: tooltips close onBlur for keyboard users. Close button is not keyboard focusable.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    dismissible: bool,
    /**
     * call back function for closing
     */
    onClose: func,
    /**
     * Duration for tooltip type message when not dismissible.
     */
    duration: number,
    /**
     * Content of InlineMessage
     */
    content: node.isRequired,
    /**
     * Value to be displayed on button
     */
    actionLabel: string,
    /**
     * Callback for extra functionality when action is clicked
     */
    onActionClick: func,
    /**
     * preventOverflow: an object variable; used to prevent InlineMessage from being positioned outside the boundary.
     *  More detail in @ids/position preventOverflow API document.
     * */
    // eslint-disable-next-line react/forbid-prop-types
    preventOverflow: object,
    /**
     * When `style` or `className` prop is provided, provide the reason behind the override.
     */
    styleOverrideReason
  };

  static defaultProps = {
    automationId: undefined,
    alignment: 'left',
    position: 'top',
    isStatic: false,
    open: false,
    dismissible: false,
    children: null,
    onClose() {},
    duration: 3000,
    onActionClick() {},
    actionLabel: '',
    preventOverflow: undefined,
    styleOverrideReason: undefined
  };

  componentDidMount() {
    if (this.props.open) {
      this.forceUpdate();
    }
  }

  componentDidUpdate() {
    if (this.props.open) {
      this.setTimer();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  onRootRef = ref => {
    this.target = ref;
  };

  setTimer = () => {
    if (!this.props.isStatic) {
      this.timer = setTimeout(this.props.onClose, this.props.duration);
    }
  };

  renderWrapper = attr => (
    <Wrapper data-automation-id={this.props.automationId} {...attr}>
      <StyledSuccessIcon />
      <Content>
        {this.props.content}
        {this.props.isStatic && this.props.open && this.props.actionLabel && (
          <StyledLinkActionButton onClick={this.props.onActionClick}>
            {this.props.actionLabel}
          </StyledLinkActionButton>
        )}
      </Content>
      {this.props.dismissible && (
        <StyledCloseIconControl onClick={this.props.onClose} />
      )}
    </Wrapper>
  );

  render() {
    const {
      isStatic,
      open,
      children,
      alignment,
      position,
      preventOverflow,
      ...restProps
    } = this.props;

    // render static message with regular div
    return isStatic && open ? (
      this.renderWrapper({ role: 'alert', ...restProps })
    ) : (
      <>
        {children && (
          <RootRef rootRef={this.onRootRef}>
            {typeof children === 'string' ? <span>{children}</span> : children}
          </RootRef>
        )}
        <IDSContext.Consumer>
          {context => (
            <ZIndex
              assignZIndex={open}
              getCurrentBaseZIndex={context.getCurrentBaseZIndex}
              incrementBaseZIndex={context.incrementBaseZIndex}
              decrementBaseZIndex={context.decrementBaseZIndex}
            >
              <Position
                show={open}
                targetElement={this.target}
                alignment={alignment}
                position={position}
                preventOverflow={preventOverflow}
              >
                {this.renderWrapper({ 'aria-live': 'polite', ...restProps })}
              </Position>
            </ZIndex>
          )}
        </IDSContext.Consumer>
      </>
    );
  }
}

InlineMessage.displayName = 'InlineMessage';
export default InlineMessage;
